"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaHypeCta, ctaBeats } from "./DevaKathaHypeCta";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Kanwar Yatra (4f64cf48) phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.
// Times ABSOLUTE seconds on the assembled narration (jyotirlinga — no opener shift). 1280x704.
// Every `start` was RESOLVED PROGRAMMATICALLY from word_timestamps_hindi (3850 words, ends 1433.93s
// vs duration 1433.94s — not drifted); none hand-typed. Verified 0 anchors >1s early.
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 57 phrase cards. Numerals stay LATIN (105, 4.5, 1,000, 29,500, 395, 24, 63, 1911, 1980, 300);
// names/terms in Devanagari. out = start + 3.4s, EXCEPT:
//   - "ज़मीन को / नहीं छू सकती" (36.09) clipped to 39.05 so it clears before "105 किलोमीटर" at 39.20
//     (two top-left holes cards on screen at once collide visually);
//   - "29,500 CCTV / 395 ड्रोन" (306.01) held 6.2s so the drone figure is still up when spoken (~310.5s).
export const KANWAR_PHRASES: Phrase[] = [

  // ── HOOK ──
  { start: 13.93,    out: 17.33,    lines: [[{ t: "उल्टी", c: "y" }, { t: "धारा", c: "w", big: true }]] },
  { start: 36.09,    out: 39.05,    lines: [[{ t: "ज़मीन", c: "w" }, { t: "को", c: "y", small: true }], [{ t: "नहीं", c: "r" }, { t: "छू", c: "r", big: true }, { t: "सकती", c: "r" }]] },
  { start: 39.20,    out: 42.60,    lines: [[{ t: "105", c: "w", big: true }, { t: "किलोमीटर", c: "y" }], [{ t: "नंगे", c: "y" }, { t: "पैर", c: "w", big: true }]] },
  { start: 48.57,    out: 51.97,    lines: [[{ t: "क़सम", c: "w" }, { t: "टूट", c: "r", big: true }, { t: "गई", c: "r" }]] },
  { start: 58.70,    out: 62.10,    lines: [[{ t: "किसी", c: "y", small: true }, { t: "ग्रंथ", c: "y" }], [{ t: "में", c: "w", small: true }, { t: "लिखा", c: "w" }, { t: "ही", c: "r", small: true }, { t: "नहीं", c: "r", big: true }]] },
  { start: 78.38,    out: 81.78,    lines: [[{ t: "नियम", c: "w" }, { t: "नहीं", c: "r", small: true }], [{ t: "यह", c: "y", small: true }, { t: "शर्त", c: "y", big: true }, { t: "है", c: "w" }]] },
  { start: 86.75,    out: 90.15,    lines: [[{ t: "लंकापति", c: "y" }], [{ t: "रावण", c: "r", big: true }]] },
  { start: 94.79,    out: 98.19,    lines: [[{ t: "धरती", c: "w", small: true }, { t: "की", c: "w", small: true }], [{ t: "पहली", c: "y" }, { t: "कांवड़", c: "w", big: true }]] },
  { start: 101.16,   out: 104.56,   lines: [[{ t: "अधूरी", c: "w", small: true }, { t: "यात्रा", c: "w", small: true }], [{ t: "तीन", c: "r", big: true }, { t: "सवाल", c: "y" }]] },
  { start: 111.11,   out: 114.51,   lines: [[{ t: "रावण", c: "y" }, { t: "के", c: "w", small: true }, { t: "अँगूठे", c: "w" }], [{ t: "का", c: "w", small: true }, { t: "निशान", c: "r", big: true }]] },

  // ── WHY WATER ──
  { start: 150.73,   out: 154.13,   lines: [[{ t: "शिव", c: "y" }, { t: "पर", c: "w", small: true }], [{ t: "जल", c: "w" }, { t: "ही", c: "y", small: true }, { t: "क्यों?", c: "r", big: true }]] },
  { start: 155.57,   out: 158.97,   lines: [[{ t: "हलाहल", c: "r", big: true }, { t: "विष", c: "w" }]] },
  { start: 166.41,   out: 169.81,   lines: [[{ t: "कंठ", c: "w", small: true }, { t: "नीला", c: "w", small: true }], [{ t: "नीलकंठ", c: "y", big: true }]] },

  // ── SCALE ──
  { start: 210.68,   out: 214.08,   lines: [[{ t: "एक", c: "w", small: true }, { t: "पश्चिम", c: "y" }], [{ t: "एक", c: "w", small: true }, { t: "पूरब", c: "y", big: true }]] },
  { start: 234.29,   out: 237.69,   lines: [[{ t: "सुल्तानगंज", c: "y" }, { t: "से", c: "w", small: true }], [{ t: "देवघर", c: "w", big: true }]] },
  { start: 245.57,   out: 248.97,   lines: [[{ t: "सुइया", c: "y" }, { t: "पहाड़", c: "w" }], [{ t: "जलेबिया", c: "y" }, { t: "मोड़", c: "w" }], [{ t: "भूतबंगला", c: "r", big: true }]] },
  { start: 277.86,   out: 281.26,   lines: [[{ t: "4.5", c: "r", big: true }, { t: "करोड़", c: "w" }], [{ t: "कनाडा", c: "y" }, { t: "से", c: "w", small: true }, { t: "ज़्यादा", c: "w" }]] },
  { start: 290.14,   out: 293.54,   lines: [[{ t: "1,000", c: "w", big: true }, { t: "करोड़", c: "y" }], [{ t: "का", c: "w", small: true }, { t: "कारोबार", c: "w" }]] },
  { start: 306.01,   out: 312.21,   lines: [[{ t: "29,500", c: "w", big: true }, { t: "CCTV", c: "y" }], [{ t: "395", c: "r", big: true }, { t: "ड्रोन", c: "w" }]] },

  // ── NIYAM ──
  { start: 328.37,   out: 331.77,   lines: [[{ t: "नंगे", c: "y" }, { t: "पैर", c: "w" }], [{ t: "चमड़ा", c: "r" }, { t: "वर्जित", c: "r", big: true }]] },
  { start: 342.98,   out: 346.38,   lines: [[{ t: "बम", c: "y", big: true }, { t: "भोला", c: "w", big: true }]] },
  { start: 349.83,   out: 353.23,   lines: [[{ t: "नाम", c: "w" }, { t: "मिटा", c: "r", big: true }], [{ t: "हैसियत", c: "w" }, { t: "मिटी", c: "r", big: true }]] },

  // ── LADDER ──
  { start: 407.86,   out: 411.26,   lines: [[{ t: "खड़ी", c: "y" }, { t: "कांवड़", c: "w", big: true }], [{ t: "रुकना", c: "w", small: true }, { t: "नहीं", c: "r", small: true }, { t: "जानती", c: "r", small: true }]] },
  { start: 426.14,   out: 429.54,   lines: [[{ t: "दांडी", c: "y" }, { t: "कांवड़", c: "w", big: true }], [{ t: "दंडवत", c: "w", small: true }, { t: "दर", c: "w", small: true }, { t: "दंडवत", c: "w", small: true }]] },
  { start: 440.35,   out: 443.75,   lines: [[{ t: "डाक", c: "y" }, { t: "कांवड़", c: "w", big: true }], [{ t: "दौड़ती", c: "r" }, { t: "है", c: "w", small: true }]] },
  { start: 457.14,   out: 460.54,   lines: [[{ t: "24", c: "r", big: true }, { t: "घंटे", c: "w" }, { t: "में", c: "w", small: true }], [{ t: "अभिषेक", c: "y", big: true }]] },

  // ── COURT ──
  { start: 497.04,   out: 500.44,   lines: [[{ t: "चार", c: "r", big: true }, { t: "दावेदार", c: "y" }]] },
  { start: 504.77,   out: 508.17,   lines: [[{ t: "श्रवण", c: "y" }, { t: "कुमार", c: "w", big: true }]] },
  { start: 524.59,   out: 527.99,   lines: [[{ t: "अयोध्या", c: "y" }, { t: "कांड", c: "w" }], [{ t: "63वाँ", c: "w", big: true }, { t: "सर्ग", c: "y" }]] },
  { start: 555.47,   out: 558.87,   lines: [[{ t: "हाथ", c: "w", small: true }, { t: "में", c: "w", small: true }], [{ t: "सिर्फ़", c: "y" }, { t: "एक", c: "w", small: true }, { t: "घड़ा", c: "w", big: true }]] },
  { start: 581.52,   out: 584.92,   lines: [[{ t: "भगवान", c: "y" }], [{ t: "परशुराम", c: "w", big: true }]] },
  { start: 624.22,   out: 627.62,   lines: [[{ t: "शिव", c: "y" }, { t: "पुराण", c: "y" }], [{ t: "कोटिरुद्र", c: "w", big: true }, { t: "संहिता", c: "w" }]] },

  // ── TAPASYA ──
  { start: 681.42,   out: 684.82,   lines: [[{ t: "पंचाग्नि", c: "r", big: true }, { t: "तप", c: "y" }]] },
  { start: 716.90,   out: 720.30,   lines: [[{ t: "नौ", c: "r", big: true }, { t: "सिर", c: "w" }], [{ t: "अग्नि", c: "y" }, { t: "में", c: "w", small: true }]] },
  { start: 736.12,   out: 739.52,   lines: [[{ t: "याद", c: "w", small: true }, { t: "रखिए", c: "w", small: true }], [{ t: "वैद्य", c: "y", big: true }]] },
  { start: 741.38,   out: 744.78,   lines: [[{ t: "वैद्यनाथ", c: "y", big: true }], [{ t: "वैद्यों", c: "w", small: true }, { t: "का", c: "w", small: true }, { t: "नाथ", c: "w", small: true }]] },

  // ── SHART ──
  { start: 757.75,   out: 761.15,   lines: [[{ t: "आत्मलिंग", c: "y", big: true }]] },
  { start: 765.83,   out: 769.23,   lines: [[{ t: "ज़मीन", c: "w" }, { t: "पर", c: "y", small: true }], [{ t: "मत", c: "r" }, { t: "रखना", c: "r", big: true }]] },

  // ── HISTORY ──
  { start: 842.73,   out: 846.13,   lines: [[{ t: "सिर्फ़", c: "w", small: true }, { t: "300", c: "r", big: true }, { t: "साल", c: "w" }], [{ t: "लिखित", c: "y", small: true }, { t: "इतिहास", c: "y", small: true }]] },
  { start: 860.66,   out: 864.06,   lines: [[{ t: "1980", c: "w", big: true }, { t: "के", c: "y", small: true }, { t: "बाद", c: "y" }], [{ t: "विस्फोट", c: "r", big: true }]] },

  // ── SHOULDERS ──
  { start: 948.42,   out: 951.82,   lines: [[{ t: "छाले", c: "r", big: true }, { t: "उनके", c: "w", small: true }], [{ t: "प्रमाणपत्र", c: "y" }]] },
  { start: 973.52,   out: 976.92,   lines: [[{ t: "भोला", c: "y", big: true }], [{ t: "महादेव", c: "w" }, { t: "का", c: "w", small: true }, { t: "नाम", c: "w" }]] },

  // ── DEOGHAR ──
  { start: 1022.62,  out: 1026.02,  lines: [[{ t: "नाग", c: "y" }, { t: "मुकुट", c: "w", big: true }]] },
  { start: 1033.19,  out: 1036.59,  lines: [[{ t: "जेल", c: "r" }, { t: "के", c: "w", small: true }, { t: "क़ैदी", c: "r", big: true }], [{ t: "गूँथते", c: "y" }, { t: "हैं", c: "w", small: true }]] },
  { start: 1043.00,  out: 1046.40,  lines: [[{ t: "1911", c: "w", big: true }, { t: "से", c: "y", small: true }], [{ t: "अटूट", c: "y" }, { t: "परंपरा", c: "w" }]] },

  // ── CLIMAX ──
  { start: 1093.56,  out: 1096.96,  lines: [[{ t: "वरुण", c: "y", big: true }, { t: "देव", c: "w" }], [{ t: "शरीर", c: "w", small: true }, { t: "में", c: "w", small: true }]] },
  { start: 1105.43,  out: 1108.83,  lines: [[{ t: "लघुशंका", c: "r", big: true }]] },
  { start: 1148.27,  out: 1151.67,  lines: [[{ t: "साधारण", c: "w", small: true }, { t: "सा", c: "w", small: true }], [{ t: "चरवाहा", c: "y", big: true }]] },
  { start: 1155.05,  out: 1158.45,  lines: [[{ t: "इसे", c: "w", small: true }, { t: "ज़मीन", c: "w" }, { t: "पर", c: "y", small: true }], [{ t: "मत", c: "r" }, { t: "रखना", c: "r", big: true }]] },
  { start: 1171.36,  out: 1174.76,  lines: [[{ t: "स्वयं", c: "w", small: true }], [{ t: "भगवान", c: "y" }, { t: "विष्णु", c: "w", big: true }]] },
  { start: 1199.91,  out: 1203.31,  lines: [[{ t: "अँगूठा", c: "r", big: true }], [{ t: "गड़ा", c: "w" }, { t: "दिया", c: "w" }]] },
  { start: 1225.77,  out: 1229.17,  lines: [[{ t: "देवघर", c: "y", big: true }], [{ t: "देवताओं", c: "w", small: true }, { t: "का", c: "w", small: true }, { t: "घर", c: "w", small: true }]] },
  { start: 1231.53,  out: 1234.93,  lines: [[{ t: "बैजू", c: "y", big: true }], [{ t: "चरवाहे", c: "w", small: true }, { t: "का", c: "w", small: true }, { t: "मंदिर", c: "w", small: true }]] },

  // ── CLOSE ──
  { start: 1273.25,  out: 1276.65,  lines: [[{ t: "नौ", c: "w", small: true }, { t: "सिर", c: "w", small: true }, { t: "काटे", c: "w", small: true }], [{ t: "अहंकार", c: "r", big: true }, { t: "नहीं", c: "r" }]] },
  { start: 1331.15,  out: 1334.55,  lines: [[{ t: "निशिता", c: "y" }, { t: "काल", c: "w", big: true }]] },
  { start: 1365.02,  out: 1368.42,  lines: [[{ t: "करोड़ों", c: "y" }, { t: "कंधों", c: "w", big: true }], [{ t: "पर", c: "w", small: true }, { t: "लिखा", c: "y" }, { t: "है", c: "w", small: true }]] },
  { start: 1393.67,  out: 1397.07,  lines: [[{ t: "सावन", c: "y" }], [{ t: "शिवरात्रि", c: "w", big: true }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-RIGHT viewing tip — two lines, yellow "Watch at 1.2x" + smaller white
// "for best experience". NO holes mask. 0->5s only.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, right: PAD, fontFamily, opacity: op,
                  display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x
      </span>
      <span style={{ color: WHITE, fontSize: Math.round(SMALL_FS * 0.7), fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        for best experience
      </span>
    </div>
  );
};

// Bottom-LEFT "आगे देखिए" teaser — SMALL_FS, yellow text, WHITE cycling dots. NO holes mask.
// Runs over the opening hook (the three questions land at ~102-123s), 3->100s.
// >40s, so build_overlay_windows.py auto-tiles it into safe sub-windows.
const AageDekhiye: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.5, out - 0.6, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const n = Math.floor((t * 1.5) % 4); // 0,1,2,3 dots
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        आगे देखिए<span style={{ display: "inline-block", width: "1.4em", textAlign: "left", color: WHITE }}>{".".repeat(n)}</span>
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CTA — the SAME Hype phone at ALL THREE beats (Yash, 2026-07-28: "at all 3 visual
// places, the same hype cta we have created should be used"). The DevaKathaLS2 pills
// that used to sit at 189.42 and 1121.49 are REMOVED — a pill and the phone collide.
//
// Every instance uses the shared <DevaKathaHypeCta/> + ctaBeats(), so the 1.885s grid,
// the badge look and the HYPED hold are identical across all three and across future
// productions. Per-beat, only the phone's inner footage changes.
//
// Each window = spoken "सब्सक्राइब" − 1.66s lead, 11.50s long:
//   CTA1  सब्सक्राइब 189.42  -> window 187.76 → 199.26   (mid-roll 1)
//   CTA2  सब्सक्राइब 1121.49 -> window 1119.83 → 1131.33 (mid-roll 2)
//   CTA3  सब्सक्राइब 1418.26 -> window 1416.60 → 1428.10 (outro; also lands SHARE on "शेयर" 1422.03)
// CTA3 is the only one whose SHARE beat has a spoken word behind it; on the mid-rolls the
// share/like/hype beats ride the same house rhythm with no narration to fight.
// ─────────────────────────────────────────────────────────────────────────────
// 🔥 PHONE FOOTAGE COMES FROM R2, NOT staticFile(). factory_4k_full pushes ONLY
// remotion-overlays/src to Modal — public/ is baked into the HF bundle at image-build
// time and is NEVER refreshed per render. A staticFile("…mp4") added today does not
// exist in the render container, OffthreadVideo then fails, the CTA clusters die, and
// factory_4k_full's all-clusters-must-succeed gate kills the whole render with no
// composite. Serving these over https removes the bundle dependency entirely.
const CTA_SEG = (n: number) => `https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/overlay_assets/kanwar_cta${n}_seg.mp4`;
const CTA_TITLE = "कांवड़ ज़मीन को क्यों नहीं छू सकती? रावण की पहली कांवड़ से बैद्यनाथ धाम तक";
const CTA_HANDLE = "@bhola_kanwariya · 2h";
const CTA_COMMENT = "बोल बम! 🙏 कांवड़ का नियम आज समझ आया";

// Master comp — holes-masked devotional phrases + non-masked UI (teaser, watch tip, LS pills).
export const KanwarYatraOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = KANWAR_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* आगे देखिए teaser (bottom-left, cycling dots) over the opening hook */}
      <AageDekhiye fps={fps} start={3.0} out={100.0} />

      {/* viewing tip (bottom-right) */}
      <WatchTag fps={fps} start={0.0} out={5.0} />

      {/* 🔥 These Sequence times MUST stay NUMERIC LITERALS — build_overlay_windows.py
          extracts overlay windows by matching numeric literals. With consts/variables it
          finds NO window and the 4K master ships without the CTA. Verified after every edit. */}
      <Sequence from={S(187.76)} durationInFrames={S(11.50)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(1)} title={CTA_TITLE}
                          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT} beats={ctaBeats()} />
      </Sequence>
      <Sequence from={S(1119.83)} durationInFrames={S(11.50)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(2)} title={CTA_TITLE}
                          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT} beats={ctaBeats()} />
      </Sequence>
      <Sequence from={S(1416.60)} durationInFrames={S(11.50)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(3)} title={CTA_TITLE}
                          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT} beats={ctaBeats()} />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
